import React from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

// ── Suppress verbose console logging ────────────────────────────────
// Security fix L-003: Prevents internal data (profiles, roles, IDs,
// SQL errors) from leaking into deployed browser consoles.
// Works in production builds AND on deployed hosts (Cloudflare Pages,
// Vercel, Netlify) even when NODE_ENV is not set correctly.
const isLocalDev = typeof window !== 'undefined' && (
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1' ||
  window.location.hostname === '[::1]'
);
const debugConsole = typeof window !== 'undefined' && (
  window.location.search.includes('debug=console')
);

if (!debugConsole && (process.env.NODE_ENV === 'production' || !isLocalDev)) {
  console.log = () => {};
  console.warn = () => {};
  console.debug = () => {};
  console.info = () => {};
  // console.error is intentionally kept for critical error reporting
}

// ── Self-healing deploy recovery ────────────────────────────────────
// When a JS/CSS chunk fails to load (stale cached index.html after a new
// deploy, old service-worker cache, flaky network), the app used to hang on
// a blank page or spinner forever — users had to manually clear cache.
// Instead: clear stale SW/cache state ONCE and reload. The sessionStorage
// guard guarantees a single retry per tab, so this can never ping-pong loop.
const CHUNK_RECOVERY_KEY = 'chunk_recovery_attempt';
const looksLikeChunkFailure = (message, src) =>
  /Loading chunk \d+ failed|ChunkLoadError|error loading dynamically imported module|Importing a module script failed|Failed to fetch dynamically imported module/i.test(message) ||
  /\.(js|css)(\?|$)/i.test(src || '');

const handlePossibleChunkFailure = (message, src) => {
  if (!looksLikeChunkFailure(message, src)) return;
  try {
    if (sessionStorage.getItem(CHUNK_RECOVERY_KEY)) return; // already retried once
    sessionStorage.setItem(CHUNK_RECOVERY_KEY, String(Date.now()));
  } catch { /* private mode: still allow the single retry */ }
  const clearSWs = (navigator.serviceWorker && navigator.serviceWorker.getRegistrations)
    ? navigator.serviceWorker.getRegistrations().then((regs) =>
        Promise.all(regs.map((r) => r.unregister())))
    : Promise.resolve();
  const clearCaches = ('caches' in window)
    ? caches.keys().then((ks) => Promise.all(ks.map((k) => caches.delete(k))))
    : Promise.resolve();
  Promise.all([clearSWs, clearCaches])
    .catch(() => {})
    .finally(() => window.location.reload());
};

// Capture phase: resource load errors on <script>/<link> don't bubble.
window.addEventListener('error', (event) => {
  const target = event?.target;
  const isResource = target && target !== window &&
    (target.tagName === 'SCRIPT' || target.tagName === 'LINK');
  if (isResource) {
    handlePossibleChunkFailure('', target.src || target.href || '');
  } else {
    handlePossibleChunkFailure(event?.message || '', '');
  }
}, true);

window.addEventListener('unhandledrejection', (event) => {
  handlePossibleChunkFailure(String(event?.reason?.message || event?.reason || ''), '');
});

// ── PWA Push Notifications ────────────────────────────────────────
// Service worker is registered for push notifications only.
// Asset caching is handled by Cloudflare's _headers.
if ('serviceWorker' in navigator) {
  // One-time sweep on the first load after a new deployment: unregister all
  // service workers and clear all caches so no stale JS chunks survive.
  // After the first load, the clean push-only SW takes over and runs normally.
  const SW_VERSION = '4.0.0'; // bump this to force-clear on next deploy
  const storageKey = 'sw_version';

  const registerCleanSW = () => {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/service-worker.js')
        .then((r) => {
          // Force update check so push-only SW replaces any old one
          r.update().catch(() => {});
        })
        .catch((e) => console.error('[PWA] SW registration failed:', e));
    });
  };

  const needsReset = (() => {
    try { return localStorage.getItem(storageKey) !== SW_VERSION; }
    catch { return true; }
  })();

  if (needsReset) {
    // First visit after deploy — nuke everything
    navigator.serviceWorker.getRegistrations().then((regs) =>
      Promise.all(regs.map((r) => r.unregister()))
    ).then(() => {
      if ('caches' in window) {
        return caches.keys().then((ks) => Promise.all(ks.map((k) => caches.delete(k))));
      }
    }).then(() => {
      try { localStorage.setItem(storageKey, SW_VERSION); } catch {}
      registerCleanSW();
    });
  } else {
    // Normal visit — just register (SW is already clean)
    registerCleanSW();
  }

  // Listen for sync messages from service worker
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'SYNC_PENDING') {
      import('./utils/offlineSync').then(({ syncAllPending }) => {
        syncAllPending();
      });
    }
  });
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
